import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import {
  AlertCircle,
  CheckCircle2,
  Clock,
  Landmark,
  Loader2,
  RotateCcw,
  Smartphone,
} from "lucide-react";
import { formatIDR } from "@kanata/shared";
import { Seo } from "@/components/Seo";
import { QrCode } from "@/components/shop/QrCode";
import { VaHandoff } from "@/components/shop/VaHandoff";
import { VaInstructions } from "@/components/shop/VaInstructions";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { checkoutApi, type ApiPaymentPage, type PaymentState } from "@/lib/api";
import { parseExpiry, planNextPoll } from "@/lib/payment-poll";
import { trackPaymentRestarted, trackPaymentUnpaid, trackPurchase } from "@/lib/tracking";

/**
 * The branded payment page.
 *
 * This is the page the whole project exists for. Until now a buyer reaching
 * checkout was redirected off kanata.id to a payment processor's hosted page —
 * the brand disappearing at the single most important moment of the purchase.
 * Here, Kanata renders the QR itself, watches it settle, and owns every outcome
 * including the unhappy ones.
 *
 * Three things shape the implementation more than anything else:
 *
 * **Guest checkout is the normal path.** There is no session. The page is
 * authorised by a token minted at checkout and carried in the URL, so an
 * unauthenticated buyer with a valid link is the ordinary case, not an edge one.
 *
 * **The buyer is on the device they are paying from.** They will leave to open
 * their banking app and come back, so the page has to survive backgrounding and
 * resume on return — polling pauses when the tab hides and picks up on
 * visibility.
 *
 * **Polling is a courtesy, not the source of truth.** The money is confirmed by
 * the reconcile poller on the API side and eventually by the processor's
 * callback. If this page never learned the answer, the order would still be
 * marked paid. So nothing here writes anything, and a polling failure is shown
 * as "still checking", never as "not paid".
 */
export default function Payment() {
  const { orderNumber = "" } = useParams();
  const [search, setSearch] = useSearchParams();
  const token = search.get("t") ?? "";

  const [page, setPage] = useState<ApiPaymentPage | null>(null);
  const [state, setState] = useState<PaymentState>("pending");
  const [expiresAt, setExpiresAt] = useState<number | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [stalled, setStalled] = useState(false);
  const [restarting, setRestarting] = useState(false);

  // ── initial load ────────────────────────────────────────────────────
  useEffect(() => {
    if (!orderNumber || !token) {
      setLoadError("Link pembayarannya tidak lengkap.");
      return;
    }
    let live = true;
    checkoutApi
      .payment(orderNumber, token)
      .then((p) => {
        if (!live) return;
        setPage(p);
        setState(p.state);
        setExpiresAt(parseExpiry(p.charge?.expiresAt));
      })
      .catch(() => {
        if (live) setLoadError("Pesanan tidak ditemukan, atau link-nya sudah tidak berlaku.");
      });
    return () => {
      live = false;
    };
  }, [orderNumber, token]);

  // ── the poll loop ───────────────────────────────────────────────────
  //
  // A chain of timeouts rather than an interval: each next delay is decided
  // from the current state by planNextPoll, which is where the rules — back off
  // on errors, stop at expiry, pause when hidden — actually live and are
  // tested. See lib/payment-poll.ts.
  const startedAt = useRef(Date.now());
  const errors = useRef(0);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const settled = state === "paid" || state === "failed";

  const schedule = useCallback(
    function schedule() {
      if (timer.current) clearTimeout(timer.current);
      const plan = planNextPoll({
        state,
        consecutiveErrors: errors.current,
        hidden: typeof document !== "undefined" && document.visibilityState === "hidden",
        now: Date.now(),
        expiresAt,
        startedAt: startedAt.current,
      });

      if (plan.delayMs === null) {
        // "expired" is our own clock catching up with a QR the server has not
        // been asked about yet. Reflecting it means the buyer is offered "start
        // over" the moment the code dies, rather than at the next poll.
        if (plan.stop === "expired") setState("expired");
        if (plan.stop === "gave-up") setStalled(true);
        return;
      }

      timer.current = setTimeout(async () => {
        try {
          const s = await checkoutApi.paymentStatus(orderNumber, token);
          errors.current = 0;
          setStalled(false);
          setState(s.state);
          const next = parseExpiry(s.expiresAt);
          if (next !== null) setExpiresAt(next);
        } catch {
          // Deliberately not surfaced as a payment failure. The order may well
          // be paid; we simply could not ask. Back off and try again.
          errors.current += 1;
          if (errors.current >= 3) setStalled(true);
        } finally {
          schedule();
        }
      }, plan.delayMs);
    },
    [state, expiresAt, orderNumber, token],
  );

  useEffect(() => {
    if (!page || settled || !token) return;
    schedule();
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [page, settled, token, schedule]);

  // The sale, reported once this browser sees it paid. The server reports it
  // too (the Conversions API), under the same order number.
  useEffect(() => {
    if (state === "paid" && page) trackPurchase(page.orderNumber, page.totalIdr);
  }, [state, page]);

  // The charge ending unpaid, once per charge: starting over is a new one.
  const reportedUnpaid = useRef<number | null>(null);
  useEffect(() => {
    if ((state !== "expired" && state !== "failed") || !page) return;
    if (reportedUnpaid.current === startedAt.current) return;
    reportedUnpaid.current = startedAt.current;
    trackPaymentUnpaid(state, page.orderNumber, page.paymentMethod, (Date.now() - startedAt.current) / 60_000);
  }, [state, page]);

  // The buyer leaves for their banking app and comes back. Polling stops while
  // the tab is hidden and asks immediately on return — the answer has usually
  // changed by then, and waiting out a backoff would show a stale QR to
  // somebody who has just paid.
  useEffect(() => {
    if (typeof document === "undefined") return;
    function onVisibility() {
      if (document.visibilityState !== "visible" || settled) return;
      errors.current = 0;
      schedule();
    }
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [schedule, settled]);

  // ── start over ──────────────────────────────────────────────────────
  //
  // A fresh charge against the SAME order, never a new order: the order already
  // exists and already holds its stock, and ordering again would hold a second
  // desk nobody asked for.
  async function onStartOver(payWith?: "QRIS" | "VA") {
    setRestarting(true);
    try {
      const fresh = await checkoutApi.recharge(orderNumber, token, payWith);
      trackPaymentRestarted(orderNumber, fresh.paymentMethod ?? payWith);
      setPage(fresh);
      setState(fresh.state);
      setExpiresAt(parseExpiry(fresh.charge?.expiresAt));
      errors.current = 0;
      startedAt.current = Date.now();
      setStalled(false);
      // The re-issued charge comes with a re-issued token. Replacing it in the
      // URL rather than holding it in state means a refresh, or a link the
      // buyer sent to their own phone, still works.
      if (fresh.statusToken) {
        setSearch({ t: fresh.statusToken }, { replace: true });
      }
    } catch {
      setLoadError("Belum bisa membuat kode baru. Coba lagi sebentar ya.");
    } finally {
      setRestarting(false);
    }
  }

  // ── render ──────────────────────────────────────────────────────────

  if (loadError) {
    return (
      <Shell>
        <StateCard
          tone="warn"
          icon={<AlertCircle className="size-6" />}
          title="Halaman pembayaran tidak bisa dibuka"
          body={loadError}
        >
          <Button asChild variant="outline">
            <Link to="/cek-pesanan">Cek status pesanan</Link>
          </Button>
        </StateCard>
      </Shell>
    );
  }

  if (!page) {
    return (
      <Shell>
        <div className="flex items-center justify-center gap-3 py-24 text-ink/50">
          <Loader2 className="size-5 animate-spin" />
          Menyiapkan pembayaran…
        </div>
      </Shell>
    );
  }

  if (state === "paid") {
    return (
      <Shell orderNumber={page.orderNumber}>
        <StateCard
          tone="good"
          icon={<CheckCircle2 className="size-6" />}
          title="Pembayaran diterima"
          body={`Terima kasih. Pesanan ${page.orderNumber} sudah kami terima dan sedang disiapkan untuk dikirim. Kami kabari lewat WhatsApp begitu ada nomor resinya.`}
        >
          <Button asChild>
            <Link to="/cek-pesanan">Lihat status pesanan</Link>
          </Button>
          <Button asChild variant="ghost">
            <Link to="/katalog">Lihat meja lainnya</Link>
          </Button>
        </StateCard>
        <Total totalIdr={page.totalIdr} />
      </Shell>
    );
  }

  if (state === "failed" || state === "expired") {
    const expired = state === "expired";
    return (
      <Shell orderNumber={page.orderNumber}>
        <StateCard
          tone="warn"
          icon={expired ? <Clock className="size-6" /> : <AlertCircle className="size-6" />}
          title={expired ? "Kode QR sudah kedaluwarsa" : "Pembayaran tidak berhasil"}
          body={
            expired
              ? "Kode QR-nya sudah lewat batas waktu. Pesanan kamu masih aman dan barangnya masih kami tahan — tinggal buat kode baru."
              : "Pembayarannya tidak selesai. Pesanan kamu masih tersimpan, jadi kamu bisa coba bayar lagi."
          }
        >
          <Button onClick={() => onStartOver()} disabled={restarting}>
            {restarting ? <Loader2 className="animate-spin" /> : <RotateCcw />}
            {restarting ? "Membuat kode baru…" : "Buat kode pembayaran baru"}
          </Button>
          <Button asChild variant="ghost">
            <Link to="/cek-pesanan">Cek status pesanan</Link>
          </Button>
        </StateCard>
        <Total totalIdr={page.totalIdr} />
      </Shell>
    );
  }

  // ── pending ─────────────────────────────────────────────────────────

  const charge = page.charge;

  // Virtual account keeps the hand-off this release. When VA direct charge
  // lands, delete VaHandoff.tsx and this branch; nothing else changes.
  //
  // A QRIS buyer can arrive here too, if the branded-QRIS switch has been turned
  // off — which is exactly what that switch is for, and why the same screen
  // serves both.
  if (charge?.kind === "redirect" && charge.redirectUrl) {
    return (
      <Shell orderNumber={page.orderNumber}>
        <VaHandoff
          orderNumber={page.orderNumber}
          totalIdr={page.totalIdr}
          redirectUrl={charge.redirectUrl}
          method={page.paymentMethod}
        />
      </Shell>
    );
  }

  // Branded VA (DEL-439): the number on this page, polled like QRIS.
  if (charge?.kind === "va" && charge.accountNumber) {
    return (
      <Shell orderNumber={page.orderNumber}>
        <VaInstructions
          bankName={charge.bankName || charge.bankCode || "bank"}
          accountNumber={charge.accountNumber}
          totalIdr={page.totalIdr}
          expiry={<Expiry expiresAt={expiresAt} countdown={page.countdown} />}
          waiting={
            stalled ? (
              <>
                <AlertCircle className="size-4 shrink-0" />
                <span>Koneksi lagi tersendat. Kalau sudah bayar, statusnya tetap kami catat.</span>
              </>
            ) : (
              <>
                <Loader2 className="size-4 shrink-0 animate-spin" />
                <span>Menunggu pembayaran — halaman ini otomatis berubah setelah kamu bayar.</span>
              </>
            )
          }
          fake={charge.fake}
          referenceNo={charge.referenceNo}
        />
      </Shell>
    );
  }

  // No charge to show. Either it was never created, or QRIS is unavailable —
  // the rollback switch with no hosted page behind it. Either way the order is
  // safe and holds its stock, so both routes forward are offered here rather
  // than sending anybody back to checkout to order a second desk.
  if (!charge?.qrPayload) {
    return (
      <Shell orderNumber={page.orderNumber}>
        <StateCard
          tone="warn"
          icon={<AlertCircle className="size-6" />}
          title="Kode pembayaran belum siap"
          body="Pesanan kamu sudah tersimpan dan barangnya masih kami tahan. Coba buat ulang kode QRIS-nya, atau bayar lewat virtual account."
        >
          <Button onClick={() => onStartOver("QRIS")} disabled={restarting}>
            {restarting ? <Loader2 className="animate-spin" /> : <RotateCcw />}
            Coba QRIS lagi
          </Button>
          <Button variant="outline" onClick={() => onStartOver("VA")} disabled={restarting}>
            <Landmark />
            Bayar lewat Virtual Account
          </Button>
        </StateCard>
        <Total totalIdr={page.totalIdr} />
      </Shell>
    );
  }

  return (
    <Shell orderNumber={page.orderNumber}>
      <div className="rounded-2xl border border-ink/8 bg-cream/60 p-6 text-center sm:p-8">
        <p className="text-sm font-medium tracking-wide text-brand uppercase">QRIS</p>
        <h2 className="mt-1 text-2xl font-medium tracking-tight text-ink">
          Scan untuk membayar
        </h2>
        <p className="mx-auto mt-2 max-w-sm text-sm leading-relaxed text-ink/60">
          Buka aplikasi apa pun yang mendukung QRIS — GoPay, OVO, DANA, ShopeePay, atau
          m-banking — lalu scan kode di bawah ini.
        </p>

        {/* A charge from the in-memory fake renders a QR that scans perfectly
            and pays nobody: the payload's checksum is deliberately invalid, so
            every banking app refuses it. Saying so here is the difference
            between reading one line and spending an evening on screen sizes
            and camera focus. Never reachable in a deployment with a real key. */}
        {charge.fake && (
          <div className="mx-auto mt-6 max-w-sm rounded-xl border border-amber-400 bg-amber-50 p-3 text-left text-xs text-amber-900">
            <p className="font-semibold">Kode QR contoh — tidak bisa dibayar.</p>
            <p className="mt-1">
              Server ini berjalan tanpa kunci Altura, jadi tidak ada tagihan sungguhan
              di belakang kode ini. Setel <code className="font-mono">ALTURA_API_KEY</code>{" "}
              lalu ulangi checkout.
            </p>
          </div>
        )}

        {/* White, always: a QR is read by contrast, and a scanner pointed at a
            cream card in a dim room is a scanner that fails without saying so. */}
        <div className="mx-auto mt-6 w-fit rounded-2xl bg-white p-4 shadow-sm ring-1 ring-ink/5">
          <QrCode payload={charge.qrPayload} size={240} />
        </div>

        <p className="mt-5 text-3xl font-medium tracking-tight text-ink tabular-nums">
          {formatIDR(page.totalIdr)}
        </p>

        <Expiry expiresAt={expiresAt} countdown={page.countdown} />

        <Separator className="my-6" />

        <div className="flex items-center justify-center gap-2 text-sm text-ink/55">
          {stalled ? (
            <>
              <AlertCircle className="size-4 shrink-0" />
              <span>Koneksi lagi tersendat. Kalau sudah bayar, statusnya tetap kami catat.</span>
            </>
          ) : (
            <>
              <Loader2 className="size-4 shrink-0 animate-spin" />
              <span>Menunggu pembayaran — halaman ini otomatis berubah setelah kamu bayar.</span>
            </>
          )}
        </div>

        <p className="mt-4 flex items-start justify-center gap-2 text-xs leading-relaxed text-ink/45">
          <Smartphone className="mt-0.5 size-3.5 shrink-0" />
          <span>
            Bayar dari HP ini? Silakan pindah ke aplikasi pembayaran kamu, lalu kembali ke
            halaman ini — statusnya akan menyusul sendiri.
          </span>
        </p>

        {charge.referenceNo && (
          <p className="mt-4 text-xs text-ink/35">Ref. {charge.referenceNo}</p>
        )}
      </div>

      <p className="mt-6 text-center text-sm text-ink/50">
        Ada kendala?{" "}
        <Link to="/kontak" className="text-brand underline underline-offset-4">
          Hubungi kami
        </Link>{" "}
        dengan menyebut nomor pesanan di atas.
      </p>
    </Shell>
  );
}

/**
 * The expiry line.
 *
 * A live countdown only when the API says so. The payment processor was, until
 * recently, reporting a QRIS expiry seven hours early — a literal +07:00 stamped
 * on a UTC clock — so a code advertised as lasting a day actually died after
 * seventeen hours. A countdown is the one element on this page that cannot
 * afford to be confidently wrong: it tells somebody they have time when they do
 * not, or panics them when they have all day. So it stays behind a server flag
 * until the expiry is verified against real credentials, and until then the page
 * says the plain thing instead.
 */
function Expiry({ expiresAt, countdown }: { expiresAt: number | null; countdown: boolean }) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!countdown || expiresAt === null) return;
    const id = setInterval(() => setNow(Date.now()), 1_000);
    return () => clearInterval(id);
  }, [countdown, expiresAt]);

  if (!countdown || expiresAt === null) {
    return <p className="mt-1 text-sm text-ink/50">Kode ini berlaku 24 jam.</p>;
  }

  const left = Math.max(0, expiresAt - now);
  const h = Math.floor(left / 3_600_000);
  const m = Math.floor((left % 3_600_000) / 60_000);
  const s = Math.floor((left % 60_000) / 1_000);
  const pad = (n: number) => String(n).padStart(2, "0");

  return (
    <p className="mt-1 flex items-center justify-center gap-1.5 text-sm text-ink/50">
      <Clock className="size-3.5" />
      Berlaku{" "}
      <span className="font-medium text-ink tabular-nums">
        {h > 0 && `${pad(h)}:`}
        {pad(m)}:{pad(s)}
      </span>{" "}
      lagi
    </p>
  );
}

function Shell({ orderNumber, children }: { orderNumber?: string; children: React.ReactNode }) {
  return (
    <>
      <Seo
        title="Pembayaran — Kanata Indonesia"
        description="Selesaikan pembayaran pesanan kamu."
        // A payment page has nothing to offer a search engine and everything to
        // lose from being indexed with a live order number in the URL.
        noindex
      />
      <main className="min-h-[70vh] pt-28 sm:pt-36">
        <div className="mx-auto max-w-lg px-5 pb-24 sm:px-8">
          {orderNumber && (
            <p className="mb-4 text-center text-sm text-ink/45">Pesanan {orderNumber}</p>
          )}
          {children}
        </div>
      </main>
    </>
  );
}

function StateCard({
  tone,
  icon,
  title,
  body,
  children,
}: {
  tone: "good" | "warn" | "plain";
  icon: React.ReactNode;
  title: string;
  body: string;
  children?: React.ReactNode;
}) {
  const toneClass =
    tone === "good"
      ? "bg-brand-light text-brand-dark"
      : tone === "warn"
        ? "bg-destructive/10 text-destructive"
        : "bg-ink/5 text-ink/70";

  return (
    <div className="rounded-2xl border border-ink/8 bg-cream/60 p-6 text-center sm:p-8">
      <div className={`mx-auto flex size-12 items-center justify-center rounded-full ${toneClass}`}>
        {icon}
      </div>
      <h2 className="mt-4 text-2xl font-medium tracking-tight text-ink">{title}</h2>
      <p className="mx-auto mt-2 max-w-sm text-sm leading-relaxed text-ink/60">{body}</p>
      {children && (
        <div className="mt-6 flex flex-col items-stretch justify-center gap-2 sm:flex-row">
          {children}
        </div>
      )}
    </div>
  );
}

function Total({ totalIdr }: { totalIdr: number }) {
  return (
    <div className="mt-4 flex items-baseline justify-between rounded-2xl border border-ink/8 px-6 py-4">
      <span className="text-sm text-ink/60">Total pesanan</span>
      <span className="text-lg font-medium text-ink tabular-nums">{formatIDR(totalIdr)}</span>
    </div>
  );
}
